'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Atom, BookOpen, BookText, Calculator, ChevronDown, Clock, ExternalLink, Landmark, Layers, MessageCircleMore, Monitor, Palette, Search, Target, X } from 'lucide-react';
import AppImage from '@/components/Image';
import PengiModal from '@/components/Modal/pengiModal';
import { toastService } from '@/components/Toast/ToastManager';
import { ParentTextbookPreviewModal } from '@/components/parent/ParentTextbookPreviewModal';
import { TextbookFeaturedSection } from '@/components/parent/TextbookFeaturedSection';
import { cdnUrl } from '@/config/domains';
import type { Quest } from '@/constant/schema';
import { apiClient } from '@/lib/api-client';
import { getQuestById } from '@/lib/questHelper';
import { buildPublicLearnHref, buildPublicPracticeHref, buildPublicSkillLearnHref, buildPublicSkillPracticeHref } from '@/lib/seo/public-slugs';
import type { TextbookTocData } from '@/lib/textbook-toc';
import type { ParentSearchItem, ParentSearchItemType, ParentSearchRequest, ParentSearchResponse } from '@/types/parent-search';

type ParentSearchResultsPanelProps = {
  query: string;
  initialData?: ParentSearchResponse | null;
  initialPreviewQuest?: Quest | null;
  initialPreviewToc?: TextbookTocData | null;
  onQueryChange: (next: string) => void;
  onDiscussWithAdvisor: (item: ParentSearchItem) => void;
  onOpenInternalResult?: (item: ParentSearchItem) => boolean | void;
  initialSubjectFilter?: string;
  initialGradeFilter?: string;
  initialDisableCorrection?: boolean;
  onSearchStateChange?: (next: { query: string; subject: string; grade: string; disableCorrection?: boolean }) => void;
  isAuthenticated?: boolean;
  canAssignActions?: boolean;
  requireAuthForInternalActions?: boolean;
  onAuthRequired?: (reason?: 'assign') => void;
  role?: 'parent' | 'teacher';
};

type SearchViewTab = 'all' | ParentSearchItemType;
type SearchActionMode = 'learn' | 'practice' | 'challenge' | 'problemSet';
type SearchActionOption = {
  key: SearchActionMode;
  label: string;
  description: string;
};
type AssignableMode = 'learn' | 'practice' | 'challenge' | 'play';
type AssignableModeOption = {
  key: AssignableMode;
  label: string;
};
type BoundStudent = {
  id: string;
  name: string;
  grade?: string | number;
};
type AssignmentDialogTarget = {
  type: 'lesson' | 'skill';
  title: string;
  questId?: string;
  chapterId?: string;
  lessonId?: string;
  skillId?: string;
  bookName?: string;
  grade?: string;
  modeOptions: AssignableModeOption[];
  selectedModes: AssignableMode[];
};

const GROUP_ORDER: ParentSearchItemType[] = ['quest', 'lesson', 'skill', 'web'];
const GROUP_META: Record<ParentSearchItemType, { label: string; icon: typeof Search }> = {
  quest: { label: 'Textbooks', icon: BookOpen },
  lesson: { label: 'Lessons', icon: Layers },
  skill: { label: 'Skills', icon: Target },
  web: { label: 'Web', icon: Search },
};
const TAB_ORDER: SearchViewTab[] = ['all', 'quest', 'lesson', 'skill', 'web'];
const LIVE_TUTOR_EXTENSION_PING = 'LT_EXTENSION_PING';
const LIVE_TUTOR_EXTENSION_PONG = 'LT_EXTENSION_PONG';
const LIVE_TUTOR_SQUARE_BADGE_URL = cdnUrl('/assets/live-tutor-square-badge.png');
const LIVE_TUTOR_BLUE_BADGE_URL = cdnUrl('/assets/live-tutor-blue-badge.png');
const PENGI_LIVE_TUTOR_CHROME_STORE_URL = 'https://chromewebstore.google.com/detail/pengi-live-tutor/lkjhjffmaaiaikckielejcaefdpbdhkg?authuser=0&hl=en&pli=1';
const parentSearchResponseCache = new Map<string, ParentSearchResponse>();
const QUICK_SEARCH_POOL = [
  'algebra basics',
  'fraction practice',
  'cell division',
  'hamilton economy',
  'ecosystems',
  'decimal multiplication',
  'multiplication arrays',
  'ratios proportions',
  'linear equations',
  'area perimeter',
  'photosynthesis',
  'states matter',
  'plate tectonics',
  'american revolution',
  'reading comprehension',
  'context clues',
  'scratch loops',
  'sprite movement',
  'equation solving',
  'triangle types',
  'fraction comparison',
  'grammar practice',
  'projectile motion',
  'energy transfer',
  'quadratic equations',
  'slope intercept',
  'coordinate plane',
  'mean median',
  'probability basics',
  'integer operations',
  'long division',
  'mixed numbers',
  'volume cubes',
  'surface area',
  'chemical reactions',
  'food chains',
  'water cycle',
  'forces motion',
  'moon phases',
  'matter changes',
  'figurative language',
  'main idea',
  'text evidence',
  'sentence structure',
  'verb tenses',
  'punctuation rules',
  'civil war',
  'ancient egypt',
  'world geography',
  'map skills',
  'constitution basics',
  'coding basics',
  'conditionals',
  'event handling',
  'debugging practice',
  'simulation lab',
] as const;
const QUICK_SEARCHES_TO_SHOW = 6;
const SUBJECT_BROWSE = [
  { label: 'Math', value: 'math', query: 'math', icon: Calculator, className: 'border-[#F87171] text-[#DC2626] bg-[#FFF5F5]' },
  { label: 'Science', value: 'science', query: 'science', icon: Atom, className: 'border-[#4ADE80] text-[#16A34A] bg-[#F4FFF7]' },
  { label: 'Reading', value: 'vocabulary', query: 'language arts', icon: BookText, className: 'border-[#F59E0B] text-[#D97706] bg-[#FFF9ED]' },
  { label: 'Coding', value: 'all', query: 'coding', icon: Monitor, className: 'border-[#818CF8] text-[#4F46E5] bg-[#F5F3FF]' },
  { label: 'History', value: 'history', query: 'history', icon: Landmark, className: 'border-[#60A5FA] text-[#2563EB] bg-[#F5F9FF]' },
  { label: 'Art', value: 'art', query: 'art', icon: Palette, className: 'border-[#F472B6] text-[#DB2777] bg-[#FFF1F8]' },
] as const;
const SUBJECT_HERO_BROWSE = [
  { label: 'MATH', value: 'math', query: 'math', className: 'border-[#F2DC00] bg-[#F2DC00] text-[#403800]' },
  { label: 'LANGUAGE ARTS', value: 'vocabulary', query: 'language arts', className: 'border-[#3D3C97] bg-[#3D3C97] text-white' },
  { label: 'SCIENCE', value: 'science', query: 'science', className: 'border-[#79AD57] bg-[#79AD57] text-white' },
  { label: 'SOCIAL STUDIES', value: 'history', query: 'history', className: 'border-[#F68B07] bg-[#F68B07] text-white' },
  { label: 'CODING', value: 'all', query: 'coding', className: 'border-[#35695F] bg-[#35695F] text-white' },
] as const;
const SUBJECT_FILTER_OPTIONS = [
  { value: 'all', label: 'All subjects' },
  { value: 'math', label: 'Math' },
  { value: 'vocabulary', label: 'Vocabulary' },
  { value: 'grammar', label: 'Grammar' },
  { value: 'science', label: 'Science' },
  { value: 'history', label: 'History / Social Studies' },
  { value: 'amc_math', label: 'AMC Math' },
] as const;
const GRADE_FILTER_OPTIONS = [
  { value: 'all', label: 'All grades' },
  { value: 'Grade 3', label: 'Grade 3' },
  { value: 'Grade 4', label: 'Grade 4' },
  { value: 'Grade 5', label: 'Grade 5' },
  { value: 'Grade 6', label: 'Grade 6' },
  { value: 'Grade 7', label: 'Grade 7' },
  { value: 'Grade 8', label: 'Grade 8' },
] as const;

function getTabLabel(tab: SearchViewTab) {
  if (tab === 'all') return 'All';
  return GROUP_META[tab].label;
}

function normalizeSearchCardText(value?: string | null) {
  return (value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function buildUniqueSegments(values: Array<string | undefined | null>) {
  const seen = new Set<string>();
  return values.filter((value): value is string => {
    const trimmed = value?.trim();
    if (!trimmed) return false;
    const normalized = normalizeSearchCardText(trimmed);
    if (!normalized || seen.has(normalized)) return false;
    seen.add(normalized);
    return true;
  });
}

function sampleQuickSearches(pool: readonly string[], count: number): string[] {
  const items = [...pool];
  for (let i = items.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [items[i], items[j]] = [items[j], items[i]];
  }
  return items.slice(0, Math.min(count, items.length));
}

function containsNormalizedText(source?: string | null, target?: string | null) {
  const sourceNormalized = normalizeSearchCardText(source);
  const targetNormalized = normalizeSearchCardText(target);
  if (!sourceNormalized || !targetNormalized) return false;
  return sourceNormalized.includes(targetNormalized);
}

function stripLessonPrefix(title?: string | null) {
  if (!title) return '';
  return title.replace(/^lesson\s*\d+\s*:\s*/i, '').trim();
}

function toTitleCaseLabel(value?: string | null) {
  if (!value) return '';
  return value
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

function FilterDropdown({
  label,
  value,
  options,
  isOpen,
  onToggle,
  onSelect,
  widthClass,
}: {
  label: string;
  value: string;
  options: ReadonlyArray<{ value: string; label: string }>;
  isOpen: boolean;
  onToggle: () => void;
  onSelect: (value: string) => void;
  widthClass: string;
}) {
  const selected = options.find((option) => option.value === value)?.label || label;

  return (
    <div className={`relative ${widthClass}`}>
      <button
        type="button"
        onClick={onToggle}
        className="flex h-[34px] w-full cursor-pointer items-center justify-between rounded-full bg-[#F5F7FF] px-4 text-[13px] font-medium text-[#3F466F] shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-colors hover:bg-white"
      >
        <span>{selected}</span>
        <ChevronDown className={`h-3.5 w-3.5 text-[#7C84B0] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen ? (
        <div className="absolute right-0 top-[calc(100%+8px)] z-20 w-full min-w-[180px] overflow-hidden rounded-[18px] bg-white p-2 shadow-[0_16px_36px_rgba(34,42,72,0.14)]">
          <div className="space-y-1">
            {options.map((option) => {
              const active = option.value === value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => onSelect(option.value)}
                  className={`flex w-full cursor-pointer items-center rounded-[12px] px-3 py-2 text-left text-[14px] transition-colors ${
                    active ? 'bg-[#EEF1FF] font-semibold text-[#2F3872]' : 'text-[#30374D] hover:bg-[#F7F8FC]'
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function buildSearchCacheKeyWithPage(
  keyword: string,
  filters: { subject: string; grade: string },
  pageToken: string,
  disableCorrection: boolean,
) {
  return JSON.stringify({
    query: keyword.trim().toLowerCase(),
    subject: filters.subject,
    grade: filters.grade,
    pageToken,
    disableCorrection,
  });
}

function withSearchPreviewParam(href: string) {
  const separator = href.includes('?') ? '&' : '?';
  return `${href}${separator}preview=1`;
}

function getLessonAssignableModes(tabConfig?: Record<string, unknown> | null): AssignableModeOption[] {
  const modes: AssignableModeOption[] = [];
  if (tabConfig?.showLearn ?? true) modes.push({ key: 'learn', label: 'Learn' });
  if (tabConfig?.showPractice ?? true) modes.push({ key: 'practice', label: 'Practice' });
  if (tabConfig?.showChallenge ?? true) modes.push({ key: 'challenge', label: 'Challenge' });
  if (tabConfig?.showPlay ?? true) modes.push({ key: 'play', label: 'Play' });
  return modes;
}

function getLessonPublicActionOptions(tabConfig?: Record<string, unknown> | null): SearchActionOption[] {
  const options: SearchActionOption[] = [];
  if (tabConfig?.showLearn ?? true) {
    options.push({ key: 'learn', label: 'Learn', description: 'Read and follow the lesson step by step.' });
  }
  if (tabConfig?.showPractice ?? true) {
    options.push({ key: 'practice', label: 'Practice', description: 'Practice the lesson with questions.' });
  }
  if (tabConfig?.showChallenge ?? true) {
    options.push({ key: 'challenge', label: 'Challenge', description: 'Try the harder challenge version.' });
  }
  if (tabConfig?.showProblemSet ?? false) {
    options.push({ key: 'problemSet', label: 'Problem Set', description: 'Work through the textbook problem set.' });
  }
  return options;
}

function openExternalUrl(url?: string) {
  if (!url || typeof window === 'undefined') return;
  window.open(url, '_blank', 'noopener,noreferrer');
}

declare global {
  interface Window {
    __PENGI_LIVE_TUTOR_EXTENSION_INSTALLED__?: boolean;
    __PENGI_LIVE_TUTOR_EXTENSION_DEBUG__?: {
      lastTimeoutMs?: number;
      lastResult?: boolean;
      lastCheckedAt?: string;
      attempts?: Array<{ timeoutMs: number; installed: boolean; checkedAt: string }>;
    };
  }
}

async function detectLiveTutorExtensionInstalled(timeoutMs = 500): Promise<boolean> {
  if (typeof window === 'undefined') return false;

  if (window.__PENGI_LIVE_TUTOR_EXTENSION_INSTALLED__) {
    console.info('[LiveTutorExtension][Search] detection short-circuited from cache');
    return true;
  }

  return new Promise<boolean>((resolve) => {
    let settled = false;

    const finish = (installed: boolean) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      window.removeEventListener('message', onMessage);
      const checkedAt = new Date().toISOString();
      const previousAttempts = window.__PENGI_LIVE_TUTOR_EXTENSION_DEBUG__?.attempts || [];
      window.__PENGI_LIVE_TUTOR_EXTENSION_DEBUG__ = {
        lastTimeoutMs: timeoutMs,
        lastResult: installed,
        lastCheckedAt: checkedAt,
        attempts: [...previousAttempts, { timeoutMs, installed, checkedAt }].slice(-10),
      };
      if (installed) {
        window.__PENGI_LIVE_TUTOR_EXTENSION_INSTALLED__ = true;
      }
      console.info('[LiveTutorExtension][Search] detection finished', { timeoutMs, installed, checkedAt });
      resolve(installed);
    };

    const onMessage = (event: MessageEvent) => {
      if (event.source !== window) return;
      if (!event.data || typeof event.data !== 'object') return;
      const payload = event.data as { type?: string };
      if (payload.type === LIVE_TUTOR_EXTENSION_PONG) {
        console.info('[LiveTutorExtension][Search] received pong', { timeoutMs });
        finish(true);
      }
    };

    console.info('[LiveTutorExtension][Search] posting ping', { timeoutMs });
    const timeoutId = window.setTimeout(() => finish(false), timeoutMs);
    window.addEventListener('message', onMessage);
    window.postMessage({ type: LIVE_TUTOR_EXTENSION_PING }, '*');
  });
}

async function detectLiveTutorExtensionInstalledWithRetry(): Promise<boolean> {
  const attempts = [500, 1200, 2000];

  for (const timeoutMs of attempts) {
    const installed = await detectLiveTutorExtensionInstalled(timeoutMs);
    console.info('[LiveTutorExtension][Search] retry attempt result', { timeoutMs, installed });
    if (installed) {
      return true;
    }
  }

  return false;
}

function getLiveTutorBadge(status: ParentSearchItem['liveTutorStatus']) {
  if (status === 'enabled') {
    return (
      <img
        src={LIVE_TUTOR_SQUARE_BADGE_URL}
        alt="Live Tutor Enabled"
        width={130}
        height={29}
        className="h-auto w-[130px] saturate-[0.75]"
      />
    );
  }
  if (status === 'coming_soon') {
    return (
      <span className="inline-flex h-7 items-center gap-1.5 rounded-full border border-[#E0E5F3] bg-[#F8FAFD] px-3 text-[12px] font-medium text-[#6A7591]">
        <Clock className="h-3 w-3" />
        Coming Soon
      </span>
    );
  }
  return null;
}

function supportsInternalLiveTutor(item: ParentSearchItem) {
  if (item.type !== 'lesson' && item.type !== 'skill') return false;
  const subject = (item.subject || '').trim().toLowerCase();
  if (!subject) return false;
  return subject === 'math' || subject === 'amc_math' || subject.includes('math');
}

function InternalLiveTutorLabel() {
  return (
    <img
      src={LIVE_TUTOR_BLUE_BADGE_URL}
      alt="Live Tutor"
      width={107}
      height={32}
      className="h-auto w-[107px] saturate-[0.75]"
    />
  );
}

function SearchEngineResultCard({
  item,
  onOpenLiveTutorSite,
}: {
  item: ParentSearchItem;
  onOpenLiveTutorSite: (item: ParentSearchItem, event: React.MouseEvent<HTMLAnchorElement>) => void;
}) {
  const isLiveTutorEnabled = item.liveTutorStatus === 'enabled';

  return (
    <article
      className={`rounded-[22px] px-4 py-3 ${
        isLiveTutorEnabled
          ? 'bg-[#F7FAFF] shadow-[0_10px_24px_rgba(78,104,166,0.06)]'
          : 'bg-transparent'
      }`}
    >
      <a
        href={item.url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(event) => {
          if (isLiveTutorEnabled) {
            onOpenLiveTutorSite(item, event);
          }
        }}
        className="group block"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            {item.favicon ? (
              <AppImage
                src={item.favicon}
                alt={item.domain || item.title}
                width={18}
                height={18}
                className="h-[18px] w-[18px] rounded"
                optimizedWidth={36}
              />
            ) : null}
            <span className="min-w-0 truncate text-xs text-[#5F6368]">
              {item.displayUrl || item.domain || item.subtitle || item.url}
            </span>
          </div>
          <div className="shrink-0">
            {getLiveTutorBadge(item.liveTutorStatus)}
          </div>
        </div>
        <div className="mt-1 flex items-start gap-1">
          <h3 className="display-text text-lg leading-6 text-[#1A0DAB] transition-colors group-hover:text-[#1558D6] group-hover:underline">
            {item.title}
          </h3>
          <ExternalLink className="mt-1 h-3.5 w-3.5 shrink-0 text-[#1A0DAB] opacity-0 transition-opacity group-hover:opacity-70" />
        </div>
        {item.description ? (
          <p className="display-text mt-1 text-sm leading-6 text-[#4B5563]">
            {item.description}
          </p>
        ) : null}
      </a>
    </article>
  );
}

function SectionLearnMoreButton({
  onClick,
}: {
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex h-8 cursor-pointer items-center justify-center rounded-full bg-[#F5F7FF] px-3.5 text-xs font-semibold text-[#355B95] transition-colors hover:bg-[#EBF0FF]"
      style={{ fontFamily: 'Rubik, sans-serif' }}
    >
      View more
    </button>
  );
}

function InternalSearchResultCard({
  item,
  onOpen,
}: {
  item: ParentSearchItem;
  onOpen: (item: ParentSearchItem) => void;
}) {
  const isTextbook = item.type === 'quest';
  const primaryTitle = isTextbook ? (item.bookName || item.title) : item.title;
  const contextTitle = item.bookName || item.questTitle || item.lessonTitle;
  const supportingTitle = isTextbook
    ? [item.questTitle, item.subtitle]
        .filter((value) => Boolean(value && value !== primaryTitle))
        .find(Boolean)
    : [item.subtitle, contextTitle]
        .filter((value) => Boolean(value && value !== primaryTitle))
        .find(Boolean);
  const metaLine = isTextbook
    ? [toTitleCaseLabel(item.subject), item.defaultGrade].filter(Boolean).join(' · ')
    : [toTitleCaseLabel(item.subject), item.defaultGrade, contextTitle]
        .filter((value) => Boolean(value && value !== supportingTitle))
        .join(' · ');

  return (
    <article className="rounded-2xl border border-[#E5E7EB] bg-white p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-colors hover:border-[#D0D7E2] hover:bg-[#FBFCFE]">
      <button
        type="button"
        onClick={() => onOpen(item)}
        className="group block w-full cursor-pointer text-left"
      >
        <div className="flex gap-4">
          {isTextbook && item.coverImage ? (
            <div className="shrink-0 overflow-hidden rounded-xl border border-[#E5E7EB] bg-[#F8FAFC]">
              <AppImage
                src={item.coverImage}
                alt={primaryTitle}
                width={84}
                height={112}
                className="h-[112px] w-[84px] object-cover"
                optimizedWidth={168}
              />
            </div>
          ) : null}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-[#EEF2FF] px-2.5 py-1 text-[11px] font-semibold text-[#4338CA]">
                {getTabLabel(item.type)}
              </span>
              {metaLine ? <span className="min-w-0 truncate text-xs text-[#64748B]">{metaLine}</span> : null}
            </div>
            <h3 className="mt-3 line-clamp-1 text-base font-semibold leading-6 text-[#111827] transition-colors group-hover:text-[#0F172A]">
              {primaryTitle}
            </h3>
            {supportingTitle ? (
              <p className="mt-2 line-clamp-2 text-sm leading-6 text-[#475569]">{supportingTitle}</p>
            ) : null}
          </div>
        </div>
      </button>
    </article>
  );
}

function LessonSearchResultCard({
  item,
  onOpen,
}: {
  item: ParentSearchItem;
  onOpen: (item: ParentSearchItem) => void;
}) {
  const metaLine = buildUniqueSegments([toTitleCaseLabel(item.subject), item.defaultGrade]).join(' · ');
  const supportText = buildUniqueSegments([item.subtitle, item.bookName, item.questTitle])[0] || '';
  const displayTitle = stripLessonPrefix(item.title) || item.title;
  const showLiveTutorLabel = supportsInternalLiveTutor(item);

  return (
    <article className="h-full rounded-[28px] border border-[#DDD8F7] bg-[#F4F0FF] transition-transform hover:-translate-y-0.5 hover:shadow-[0_14px_28px_rgba(64,52,122,0.10)]">
      <button
        type="button"
        onClick={() => onOpen(item)}
        className="group flex h-full min-h-[136px] w-full cursor-pointer flex-col rounded-[28px] bg-[#F4F0FF] px-7 py-5 text-left"
      >
        <div className="flex min-h-[32px] items-start justify-between gap-4">
          <div className="pt-[12px]">
            <div className="h-[4px] w-[50px] shrink-0 rounded-full bg-[#8E73F8]" />
          </div>
          {showLiveTutorLabel ? <InternalLiveTutorLabel /> : <div className="h-[32px] w-[107px] shrink-0" />}
        </div>
        <h3 className="mt-4 line-clamp-2 max-w-[78%] text-[17px] font-semibold leading-[1.24] text-[#282351] transition-colors group-hover:text-[#4E44B8] md:text-[18px]">
          {displayTitle}
        </h3>
        {supportText ? (
          <p className="mt-3 line-clamp-2 text-[14px] leading-[1.5] text-[#6F6798] md:text-[15px]">
            {buildUniqueSegments([metaLine, supportText]).join(' · ')}
          </p>
        ) : null}
        {!supportText && metaLine ? (
          <p className="mt-3 text-[14px] leading-[1.5] text-[#6F6798] md:text-[15px]">
            {metaLine}
          </p>
        ) : null}
      </button>
    </article>
  );
}

function SkillSearchResultCard({
  item,
  onOpen,
}: {
  item: ParentSearchItem;
  onOpen: (item: ParentSearchItem) => void;
}) {
  const previewCards = item.skillCards?.slice(0, 3) || [];
  const metaLine = buildUniqueSegments([toTitleCaseLabel(item.subject), item.defaultGrade]).join(' · ');
  const supportText = buildUniqueSegments([item.subtitle, item.bookName, item.questTitle])[0] || '';
  const showLiveTutorLabel = supportsInternalLiveTutor(item);
  const filteredPreviewCards = buildUniqueSegments(previewCards).filter(
    (card) =>
      !containsNormalizedText(card, item.title) &&
      !containsNormalizedText(card, metaLine) &&
      !containsNormalizedText(card, supportText),
  );

  return (
    <article className="h-full rounded-[24px] border border-[#D9E6FA] bg-[#EEF5FF] transition-transform hover:-translate-y-0.5 hover:shadow-[0_12px_22px_rgba(40,86,160,0.08)]">
      <button
        type="button"
        onClick={() => onOpen(item)}
        className="group flex h-full min-h-[124px] w-full cursor-pointer flex-col rounded-[24px] bg-[#EEF5FF] px-5 py-4 text-left"
      >
        <div className="flex min-h-[32px] items-start justify-between gap-4">
          <div className="pt-[12px]">
            <div className="h-[4px] w-[50px] shrink-0 rounded-full bg-[#5A95F5]" />
          </div>
          {showLiveTutorLabel ? <InternalLiveTutorLabel /> : <div className="h-[32px] w-[107px] shrink-0" />}
        </div>
        <h3 className="mt-4 line-clamp-2 max-w-[78%] text-[15px] font-semibold leading-[1.32] text-[#183A67] transition-colors group-hover:text-[#2758B5]">
          {item.title}
        </h3>
        {metaLine ? (
          <p className="mt-3 text-[12px] leading-5 text-[#6880A5]">
            {metaLine}
          </p>
        ) : null}
        {filteredPreviewCards.length > 0 ? (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {filteredPreviewCards.map((card, index) => (
              <span
                key={`${item.id}-${card}-${index}`}
                className="inline-flex items-center rounded-full bg-white px-2.5 py-1 text-[10px] font-medium text-[#56739F]"
              >
                {card}
              </span>
            ))}
          </div>
        ) : null}
        {supportText ? (
          <p className="mt-auto pt-3 line-clamp-3 text-[12px] leading-5 text-[#7188AF]">
            {supportText}
          </p>
        ) : null}
        {!filteredPreviewCards.length && !supportText ? (
          <p className="mt-4 text-[12px] leading-5 text-[#6E84AA] md:text-[13px]">Interactive skill cards ready to preview.</p>
        ) : null}
      </button>
    </article>
  );
}


function TextbookSearchCard({
  item,
  onOpen,
}: {
  item: ParentSearchItem;
  onOpen: (item: ParentSearchItem) => void;
}) {
  const title = item.bookName || item.title;
  const metaLine = [toTitleCaseLabel(item.subject), item.defaultGrade].filter(Boolean).join(' · ');

  return (
    <article className="h-full">
      <button
        type="button"
        onClick={() => onOpen(item)}
        className="group flex h-full min-h-[272px] w-full cursor-pointer flex-col items-center rounded-[22px] border border-[#D8E3F0] bg-[#F2F6FB] px-4 py-4 text-center transition-transform hover:-translate-y-0.5 hover:bg-[#EAF1F8]"
      >
        <div className="mt-[10px] overflow-hidden rounded-[16px] bg-white shadow-[0_10px_18px_rgba(59,86,120,0.12)]">
          {item.coverImage ? (
            <AppImage
              src={item.coverImage}
              alt={title}
              width={136}
              height={184}
              className="h-[184px] w-[136px] object-cover"
              optimizedWidth={272}
            />
          ) : (
            <div className="flex h-[184px] w-[136px] items-center justify-center bg-[linear-gradient(180deg,#EEF2FF_0%,#E2E8F0_100%)] px-4">
              <span className="line-clamp-4 text-base font-semibold leading-6 text-[#334155]">{title}</span>
            </div>
          )}
        </div>
        <div className="mt-[19px] w-full">
          <div className="flex justify-center">
            <h3 className="line-clamp-2 text-[15px] font-semibold leading-[1.35] text-[#2B415D]">{title}</h3>
          </div>
          <div className="mt-[2px] min-h-[18px]">
            {metaLine ? <p className="text-[13px] leading-5 text-[#6F83A0]">{metaLine}</p> : null}
          </div>
          <div className="mt-[2px] flex justify-center">
            <div className="h-[4px] w-[46px] rounded-full bg-[#7D9AC2]" />
          </div>
        </div>
      </button>
    </article>
  );
}

function SearchSectionSkeleton({ type }: { type: ParentSearchItemType }) {
  if (type === 'quest') {
    return (
      <section className="rounded-xl border border-[#E5E7EB] bg-[#F8F8F8] p-3">
        <div className="mb-3 inline-flex items-center rounded-md bg-[#ECECEC] px-2.5 py-1 text-[12px] font-medium text-[#5C5C5C]">
          Textbook
        </div>
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-6">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={`quest-skeleton-${index}`} className="h-[220px] animate-pulse rounded-xl bg-[#ECEFF4]" />
          ))}
        </div>
      </section>
    );
  }

  if (type === 'web') {
    return (
      <section className="rounded-xl border border-[#E5E7EB] bg-[#F8F8F8] p-3">
        <div className="mb-3 inline-flex items-center rounded-md bg-[#ECECEC] px-2.5 py-1 text-[12px] font-medium text-[#5C5C5C]">
          Web
        </div>
        <div className="rounded-lg bg-white p-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={`web-skeleton-${index}`} className="mb-3 h-16 animate-pulse rounded-lg bg-[#ECEFF4] last:mb-0" />
          ))}
        </div>
      </section>
    );
  }

  return (
    <section className="rounded-xl border border-[#E5E7EB] bg-[#F8F8F8] p-3">
      <div className="mb-3 inline-flex items-center rounded-md bg-[#ECECEC] px-2.5 py-1 text-[12px] font-medium text-[#5C5C5C]">
        {GROUP_META[type].label}
      </div>
      <div className="grid gap-3">
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={`${type}-skeleton-${index}`} className="h-[92px] animate-pulse rounded-xl bg-[#ECEFF4]" />
        ))}
      </div>
    </section>
  );
}

function ExtensionPromoCard({ compact = false }: { compact?: boolean }) {
  if (compact) {
    return (
      <div className="mx-auto w-full max-w-[860px] rounded-[22px] bg-white px-5 py-[8px] shadow-[0_12px_26px_rgba(34,42,72,0.07)]" style={{ fontFamily: 'Rubik, sans-serif' }}>
        <div className="flex flex-col gap-2 md:min-h-[58px] md:flex-row md:items-center md:justify-between">
          <div className="flex min-w-0 items-center gap-4">
            <div className="shrink-0">
              <span className="inline-flex h-7 items-center rounded-full bg-[#EEF1FF] px-3 text-[10px] font-semibold text-[#5A60E8]">
                Chrome Extension
              </span>
            </div>
            <p className="min-w-0 text-[14px] font-semibold leading-5 text-[#222A48]">
              Get voice and whiteboard help on common learning sites.
            </p>
          </div>
          <a
            href={PENGI_LIVE_TUTOR_CHROME_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex h-10 min-w-[156px] shrink-0 items-center justify-center rounded-full border border-[#222A48] bg-[#222A48] px-5 text-[13px] font-semibold text-white shadow-[0_10px_18px_rgba(34,42,72,0.18)] transition-all hover:translate-y-[-1px] hover:opacity-95"
          >
            Add to Chrome
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full overflow-hidden rounded-2xl bg-white p-4 shadow-[0_16px_30px_rgba(34,42,72,0.07)]" style={{ fontFamily: 'Rubik, sans-serif' }}>
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-[17px] font-medium leading-tight text-[#222A48]">
            Install the Pengi Live Tutor Chrome Extension for voice and whiteboard help on common learning sites.
          </p>
        </div>
        <div className="w-full md:w-[320px]">
          <a
            href={PENGI_LIVE_TUTOR_CHROME_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mx-auto inline-flex h-10 w-[191px] items-center justify-center rounded-[999px] border border-[#222A48] bg-[#222A48] px-4 text-[14px] font-medium text-white shadow-[0_10px_16px_rgba(34,42,72,0.18)] transition-opacity hover:opacity-90"
          >
            Add to Chrome
          </a>
        </div>
      </div>
    </div>
  );
}

export function ParentSearchResultsPanel({
  query,
  initialData = null,
  initialPreviewQuest = null,
  initialPreviewToc = null,
  onQueryChange,
  onDiscussWithAdvisor,
  onOpenInternalResult,
  initialSubjectFilter = 'all',
  initialGradeFilter = 'all',
  initialDisableCorrection = false,
  onSearchStateChange,
  isAuthenticated = true,
  canAssignActions = true,
  requireAuthForInternalActions = false,
  onAuthRequired,
  role = 'parent',
}: ParentSearchResultsPanelProps) {
  const requestSeqRef = useRef(0);
  const inFlightSearchKeysRef = useRef(new Set<string>());
  const isSearchInputComposingRef = useRef(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState<ParentSearchResponse | null>(null);
  const [inputValue, setInputValue] = useState(query);
  const [subjectFilter, setSubjectFilter] = useState<string>(initialSubjectFilter);
  const [gradeFilter, setGradeFilter] = useState<string>(initialGradeFilter);
  const [disableCorrection, setDisableCorrection] = useState(initialDisableCorrection);
  const [activeTab, setActiveTab] = useState<SearchViewTab>('all');
  const [activeQuestId, setActiveQuestId] = useState<string | null>(null);
  const [activeChapterId, setActiveChapterId] = useState<string | null>(null);
  const [activeLessonId, setActiveLessonId] = useState<string | null>(null);
  const [autoOpenAssign, setAutoOpenAssign] = useState(false);
  const [liveTutorInstallItem, setLiveTutorInstallItem] = useState<ParentSearchItem | null>(null);
  const [liveTutorExtensionInstalled, setLiveTutorExtensionInstalled] = useState<boolean | null>(null);
  const [webCurrentPage, setWebCurrentPage] = useState(1);
  const quickSearches = useMemo(() => sampleQuickSearches(QUICK_SEARCH_POOL, QUICK_SEARCHES_TO_SHOW), []);
  const [webPageTokens, setWebPageTokens] = useState<string[]>(['']);
  const [activeActionItem, setActiveActionItem] = useState<ParentSearchItem | null>(null);
  const [activeActionOptions, setActiveActionOptions] = useState<SearchActionOption[]>([]);
  const [activeAssignModeOptions, setActiveAssignModeOptions] = useState<AssignableModeOption[]>([]);
  const [activeActionError, setActiveActionError] = useState('');
  const [isLoadingActionOptions, setIsLoadingActionOptions] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [openFilterMenu, setOpenFilterMenu] = useState<'subject' | 'grade' | null>(null);
  const [boundStudents, setBoundStudents] = useState<BoundStudent[]>([]);
  const [isLoadingStudents, setIsLoadingStudents] = useState(false);
  const [selectedChildId, setSelectedChildId] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);
  const [assignMessage, setAssignMessage] = useState<string | null>(null);
  const [assignTarget, setAssignTarget] = useState<AssignmentDialogTarget | null>(null);

  const groupedResults = useMemo(() => {
    return {
      quest: data?.groups.quest || [],
      lesson: data?.groups.lesson || [],
      skill: data?.groups.skill || [],
      web: data?.groups.web || [],
    };
  }, [data]);

  const hasResults = useMemo(() => {
    return GROUP_ORDER.some((type) => groupedResults[type].length > 0);
  }, [groupedResults]);
  const correctionBanner = useMemo(() => {
    if (!data?.meta.correctionApplied || !data.meta.correctedQuery || !data.meta.originalQuery) {
      return null;
    }
    return {
      originalQuery: data.meta.originalQuery,
      correctedQuery: data.meta.correctedQuery,
    };
  }, [data]);
  const hasEnoughQuery = query.trim().length >= 2;
  const shouldShowResultPanel = isLoading || Boolean(error) || (hasEnoughQuery && hasResults) || (hasEnoughQuery && !hasResults);
  const activeTabHasResults = activeTab === 'all' ? hasResults : groupedResults[activeTab].length > 0;

  useEffect(() => {
    setInputValue(query);
  }, [query]);

  useEffect(() => {
    setSubjectFilter(initialSubjectFilter);
  }, [initialSubjectFilter]);

  useEffect(() => {
    setGradeFilter(initialGradeFilter);
  }, [initialGradeFilter]);

  useEffect(() => {
    setDisableCorrection(initialDisableCorrection);
  }, [initialDisableCorrection]);

  useEffect(() => {
    if (!openFilterMenu) return;

    const handleClickOutside = () => setOpenFilterMenu(null);
    window.addEventListener('click', handleClickOutside);
    return () => window.removeEventListener('click', handleClickOutside);
  }, [openFilterMenu]);

  useEffect(() => {
    if (!initialData) return;

    const cacheKey = buildSearchCacheKeyWithPage(
      initialData.query,
      { subject: initialSubjectFilter, grade: initialGradeFilter },
      initialData.meta.web?.pageToken || '',
      initialDisableCorrection,
    );

    parentSearchResponseCache.set(cacheKey, initialData);
    setData(initialData);
    setError('');
    setIsLoading(false);
  }, [initialData, initialDisableCorrection, initialGradeFilter, initialSubjectFilter]);

  const runSearch = useCallback(async (keyword: string, filters: { subject: string; grade: string }, pageToken = '') => {
    const normalized = keyword.trim();
    if (normalized.length < 2) {
      setData(null);
      setError('');
      setIsLoading(false);
      return;
    }

    const cacheKey = buildSearchCacheKeyWithPage(normalized, filters, pageToken, disableCorrection);
    const cached = parentSearchResponseCache.get(cacheKey);
    if (cached) {
      setData(cached);
      setError('');
      setIsLoading(false);
      return;
    }
    if (inFlightSearchKeysRef.current.has(cacheKey)) {
      return;
    }

    const seq = requestSeqRef.current + 1;
    requestSeqRef.current = seq;
    inFlightSearchKeysRef.current.add(cacheKey);

    setIsLoading(true);
    setError('');
    try {
      const payloadBody: ParentSearchRequest = {
        query: normalized,
        topN: 30,
        mode: 'hybrid',
        pageToken: pageToken || undefined,
        disableCorrection,
        filters: {
          subject: filters.subject === 'all' ? [] : [filters.subject],
          grade: filters.grade === 'all' ? [] : [filters.grade],
        },
      };

      const abortController = new AbortController();
      // External web-source aggregation can be slow in dev/proxy environments.
      const timeoutId = window.setTimeout(() => abortController.abort(), 20000);
      let response: Response;
      try {
        response = await apiClient.post('/api/parent/search/site', payloadBody, {
          skipAuth: true,
          signal: abortController.signal,
        });
      } finally {
        window.clearTimeout(timeoutId);
      }

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload?.error || 'Search request failed');
      }

      const payload = (await response.json()) as ParentSearchResponse;
      if (requestSeqRef.current !== seq) return;
      parentSearchResponseCache.set(cacheKey, payload);
      setData(payload);
    } catch (err) {
      if (requestSeqRef.current !== seq) return;
      const message = err instanceof Error
        ? (err.name === 'AbortError' ? 'Search timed out. Please try again.' : err.message)
        : 'Search failed';
      setError(message);
      setData(null);
    } finally {
      inFlightSearchKeysRef.current.delete(cacheKey);
      if (requestSeqRef.current === seq) {
        setIsLoading(false);
      }
    }
  }, [disableCorrection]);

  useEffect(() => {
    setWebCurrentPage(1);
    setWebPageTokens(['']);
    void runSearch(query, { subject: subjectFilter, grade: gradeFilter }, '');
  }, [query, runSearch, subjectFilter, gradeFilter]);

  useEffect(() => {
    let isMounted = true;
    void detectLiveTutorExtensionInstalledWithRetry().then((installed) => {
      if (!isMounted) return;
      setLiveTutorExtensionInstalled(installed);
    });
    return () => {
      isMounted = false;
    };
  }, []);

  const buildActionHref = useCallback((item: ParentSearchItem, mode: SearchActionMode) => {
    if (item.type === 'skill') {
      if (mode === 'learn') return withSearchPreviewParam(buildPublicSkillLearnHref(item.title, item.id));
      if (mode === 'challenge') return withSearchPreviewParam(buildPublicSkillPracticeHref(item.title, item.id, 'challenge'));
      return withSearchPreviewParam(buildPublicSkillPracticeHref(item.title, item.id));
    }

    const lessonId = item.lessonId || item.id;
    if (mode === 'learn') return withSearchPreviewParam(buildPublicLearnHref(item.title, lessonId));
    if (mode === 'challenge') return withSearchPreviewParam(buildPublicPracticeHref(item.title, lessonId, 'challenge'));
    if (mode === 'problemSet') return withSearchPreviewParam(buildPublicPracticeHref(item.title, lessonId, 'problemSet'));
    return withSearchPreviewParam(buildPublicPracticeHref(item.title, lessonId));
  }, []);

  const openActionModalForItem = useCallback(async (item: ParentSearchItem) => {
    if (item.type === 'skill') {
      const modes = item.skillAvailableModes || [];
      const options: SearchActionOption[] = [];
      const assignModeOptions: AssignableModeOption[] = [];

      if (modes.includes('learn')) {
        options.push({ key: 'learn', label: 'Learn', description: 'Read and follow the skill step by step.' });
        assignModeOptions.push({ key: 'learn', label: 'Learn' });
      }
      if (modes.includes('practice')) {
        options.push({ key: 'practice', label: 'Practice', description: 'Practice questions for this skill.' });
        assignModeOptions.push({ key: 'practice', label: 'Practice' });
      }
      if (modes.includes('challenge')) {
        options.push({ key: 'challenge', label: 'Challenge', description: 'Try the harder challenge version.' });
        assignModeOptions.push({ key: 'challenge', label: 'Challenge' });
      }

      setActiveActionItem(item);
      setActiveActionOptions(options);
      setActiveAssignModeOptions(assignModeOptions);
      setActiveActionError(options.length === 0 ? 'No public study modes are available for this skill yet.' : '');
      return;
    }

    if (item.type === 'lesson') {
      setActiveActionItem(item);
      setActiveActionOptions([]);
      setActiveActionError('');
      setIsLoadingActionOptions(true);

      try {
        const quest = item.questId ? await getQuestById(item.questId) : undefined;
        const options = getLessonPublicActionOptions(quest?.tab_config as Record<string, unknown> | undefined);
        const assignModeOptions = getLessonAssignableModes(quest?.tab_config as Record<string, unknown> | undefined);

        setActiveActionOptions(options);
        setActiveAssignModeOptions(assignModeOptions);
        setActiveActionError(options.length === 0 ? 'No public study modes are available for this lesson yet.' : '');
      } catch (error) {
        console.error('Failed to load lesson action options:', error);
        setActiveActionError('Failed to load lesson study modes.');
        setActiveAssignModeOptions([]);
      } finally {
        setIsLoadingActionOptions(false);
      }
    }
  }, []);

  const resetActionModal = useCallback(() => {
    setActiveActionItem(null);
    setActiveActionOptions([]);
    setActiveAssignModeOptions([]);
    setActiveActionError('');
    setIsLoadingActionOptions(false);
  }, []);

  const resetAssignDialog = useCallback(() => {
    setShowAssignDialog(false);
    setBoundStudents([]);
    setIsLoadingStudents(false);
    setSelectedChildId('');
    setIsAssigning(false);
    setAssignMessage(null);
    setAssignTarget(null);
  }, []);

  const openAssignDialog = useCallback(async (target: AssignmentDialogTarget) => {
    setAssignTarget(target);
    setAssignMessage(null);
    setShowAssignDialog(true);
    setIsLoadingStudents(true);
    try {
      let students: BoundStudent[];
      if (role === 'teacher') {
        const response = await apiClient.get('/api/teacher-tools/students');
        const payload = await response.json();
        if (!response.ok) throw new Error(payload?.error || 'Failed to load students');
        students = (payload?.students || []).map((s: any) => ({ id: s.id, name: s.name, grade: s.grade }));
      } else {
        const response = await apiClient.get('/api/parent-tools/student-binding');
        const payload = await response.json();
        if (!response.ok) throw new Error(payload?.error || 'Failed to load students');
        students = (payload?.students || []) as BoundStudent[];
      }
      setBoundStudents(students);
      setSelectedChildId(role === 'teacher' && students.length > 0 ? '__all__' : (students[0]?.id || ''));
    } catch (error: any) {
      setBoundStudents([]);
      setSelectedChildId('');
      setAssignMessage(error?.message || 'Failed to load students');
    } finally {
      setIsLoadingStudents(false);
    }
  }, [role]);

  const handleAssignAction = useCallback(async () => {
    if (!assignTarget || !selectedChildId || isAssigning) {
      return;
    }

    setIsAssigning(true);
    setAssignMessage(null);

    const targetStudentIds = selectedChildId === '__all__'
      ? boundStudents.map((s) => s.id)
      : [selectedChildId];

    try {
      const endpoint = assignTarget.type === 'skill'
        ? '/api/parent-tools/skill-assignment'
        : '/api/parent-tools/lesson-assignment';

      let successCount = 0;
      for (const childId of targetStudentIds) {
        const body = assignTarget.type === 'skill'
          ? {
              child_id: childId,
              skill_id: assignTarget.skillId,
              skill_title: assignTarget.title,
              grade: assignTarget.grade || null,
              modes: assignTarget.selectedModes.filter((mode): mode is Exclude<AssignableMode, 'play'> => mode !== 'play'),
            }
          : {
              child_id: childId,
              quest_id: assignTarget.questId,
              chapter_id: assignTarget.chapterId,
              lesson_id: assignTarget.lessonId,
              lesson_title: assignTarget.title,
              book_name: assignTarget.bookName,
              grade: assignTarget.grade || null,
              modes: assignTarget.selectedModes,
            };

        const response = await apiClient.post(endpoint, body);
        const payload = await response.json();
        if (!response.ok) {
          throw new Error(payload?.error || `Failed to assign ${assignTarget.type}`);
        }
        successCount++;
      }

      const label = assignTarget.type === 'skill' ? 'Skill' : 'Lesson';
      const msg = targetStudentIds.length > 1
        ? `${label} assigned to ${successCount} students`
        : `${label} assigned successfully`;
      toastService.success(msg);
      resetAssignDialog();
    } catch (error: any) {
      setAssignMessage(error?.message || `Failed to assign ${assignTarget.type === 'skill' ? 'skill' : 'lesson'}`);
    } finally {
      setIsAssigning(false);
    }
  }, [assignTarget, boundStudents, isAssigning, resetAssignDialog, selectedChildId]);

  const handleInternalItemClick = useCallback((item: ParentSearchItem) => {
    if (onOpenInternalResult) {
      const handled = onOpenInternalResult(item);
      if (handled !== false) {
        return;
      }
    }

    if (requireAuthForInternalActions && !isAuthenticated) {
      onAuthRequired?.();
      return;
    }

    if (item.type === 'quest') {
      setActiveQuestId(item.questId || item.id);
      setActiveChapterId(null);
      setActiveLessonId(null);
      setAutoOpenAssign(false);
      return;
    }
    if (item.type === 'lesson' || item.type === 'skill') {
      void openActionModalForItem(item);
    }
  }, [isAuthenticated, onAuthRequired, onOpenInternalResult, openActionModalForItem, requireAuthForInternalActions]);

  const handleOpenLiveTutorSite = useCallback(async (item: ParentSearchItem, event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();

    if (liveTutorExtensionInstalled) {
      openExternalUrl(item.url);
      return;
    }

    const installed = await detectLiveTutorExtensionInstalledWithRetry();
    setLiveTutorExtensionInstalled(installed);

    if (installed) {
      openExternalUrl(item.url);
      return;
    }

    setLiveTutorInstallItem(item);
  }, [liveTutorExtensionInstalled]);

  const handleWebNextPage = useCallback(() => {
    const nextToken = data?.meta.web?.nextPageToken;
    if (!nextToken || isLoading) return;

    const nextPage = webCurrentPage + 1;
    setWebPageTokens((prev) => {
      if (prev[nextPage - 1] === nextToken) {
        return prev;
      }
      return [...prev.slice(0, nextPage - 1), nextToken];
    });
    setWebCurrentPage(nextPage);
    void runSearch(query, { subject: subjectFilter, grade: gradeFilter }, nextToken);
  }, [data?.meta.web?.nextPageToken, gradeFilter, isLoading, query, runSearch, subjectFilter, webCurrentPage]);

  const handleWebPrevPage = useCallback(() => {
    if (webCurrentPage <= 1 || isLoading) return;

    const previousPage = webCurrentPage - 1;
    const previousToken = webPageTokens[previousPage - 1] || '';
    setWebCurrentPage(previousPage);
    void runSearch(query, { subject: subjectFilter, grade: gradeFilter }, previousToken);
  }, [gradeFilter, isLoading, query, runSearch, subjectFilter, webCurrentPage, webPageTokens]);

  const commitSearch = useCallback((nextQuery: string, nextSubject = subjectFilter, nextGrade = gradeFilter) => {
    const normalized = nextQuery.trim();
    setActiveTab('all');
    setWebCurrentPage(1);
    setWebPageTokens(['']);
    setDisableCorrection(false);
    onQueryChange(normalized);
    onSearchStateChange?.({
      query: normalized,
      subject: nextSubject,
      grade: nextGrade,
      disableCorrection: false,
    });
  }, [gradeFilter, onQueryChange, onSearchStateChange, subjectFilter]);

  const handleQuickSearch = useCallback((nextQuery: string) => {
    setInputValue(nextQuery);
    commitSearch(nextQuery);
  }, [commitSearch]);

  const handleSubjectBrowse = useCallback((subject: string, nextQuery: string) => {
    setSubjectFilter(subject);
    setInputValue(nextQuery);
    commitSearch(nextQuery, subject, gradeFilter);
  }, [commitSearch, gradeFilter]);

  const handleSearchCompositionStart = useCallback(() => {
    isSearchInputComposingRef.current = true;
  }, []);

  const handleSearchCompositionEnd = useCallback(() => {
    isSearchInputComposingRef.current = false;
  }, []);

  const handleSearchInputKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return;
    if (isSearchInputComposingRef.current || event.nativeEvent.isComposing) {
      return;
    }

    event.preventDefault();
    commitSearch(inputValue);
  }, [commitSearch, inputValue]);

  useEffect(() => {
    const handleWindowFocus = () => {
      void detectLiveTutorExtensionInstalledWithRetry().then((installed) => {
        setLiveTutorExtensionInstalled(installed);
      });
    };

    window.addEventListener('focus', handleWindowFocus);
    return () => window.removeEventListener('focus', handleWindowFocus);
  }, []);

  return (
    <div
      className="mx-auto w-full max-w-[1120px] space-y-5"
      style={{ fontFamily: 'Rubik, sans-serif' }}
    >
      <PengiModal
        visible={Boolean(liveTutorInstallItem)}
        onClick={() => setLiveTutorInstallItem(null)}
        showPengiLogo={false}
        showFooterButton={false}
        width="520px"
      >
        <div className="w-full text-left">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="display-text-bold text-xl text-[#111827]">Install Pengi Live Tutor</h3>
              <p className="display-text mt-2 text-sm leading-6 text-[#4B5563]">
                To open <span className="font-semibold text-[#111827]">{liveTutorInstallItem?.title || 'this site'}</span> with Live Tutor,
                install the Pengi browser extension first.
              </p>
            </div>
          </div>
          <div className="mt-5 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => {
                if (liveTutorInstallItem?.url) {
                  openExternalUrl(liveTutorInstallItem.url);
                }
                setLiveTutorInstallItem(null);
              }}
              className="cursor-pointer rounded-full border border-[#D1D5DB] bg-white px-4 py-2 text-sm font-medium text-[#374151] hover:bg-[#F9FAFB]"
            >
              Continue to website
            </button>
            <a
              href={PENGI_LIVE_TUTOR_CHROME_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center rounded-full bg-[#FA6467] px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
            >
              Install Free
            </a>
          </div>
        </div>
      </PengiModal>

      <ParentTextbookPreviewModal
        questId={activeQuestId}
        initialQuest={activeQuestId && initialPreviewQuest?.id === activeQuestId ? initialPreviewQuest : null}
        initialToc={activeQuestId && initialPreviewQuest?.id === activeQuestId ? initialPreviewToc : null}
        chapterId={activeChapterId}
        lessonId={activeLessonId}
        autoOpenAssign={autoOpenAssign}
        allowAssignActions={canAssignActions}
        visible={Boolean(activeQuestId)}
        onAuthRequired={onAuthRequired}
        onClose={() => {
          setActiveQuestId(null);
          setActiveChapterId(null);
          setActiveLessonId(null);
          setAutoOpenAssign(false);
        }}
      />
      <PengiModal
        visible={Boolean(activeActionItem)}
        onClick={resetActionModal}
        showFooterButton={false}
        width="440px"
      >
        <div className="w-full text-left">
          {activeActionItem ? (
            <div className="mt-1">
              <div className="relative overflow-hidden rounded-[24px] bg-white px-6 pt-4 pb-6 shadow-[0_10px_24px_rgba(34,42,72,0.06)]">
                <div className="flex flex-col gap-3.5">
                  <div className="min-w-0">
                    <div className="text-center text-[16px] font-semibold leading-[1.5] text-[#111827]">
                      <span className="not-italic">
                        {activeActionItem.type === 'skill' ? 'Skill: ' : 'Lesson: '}
                      </span>
                      <span>
                        {stripLessonPrefix(activeActionItem.title) || activeActionItem.title}
                      </span>
                    </div>
                    {!isLoadingActionOptions && !activeActionError ? (
                      <div className="mt-[24px] flex flex-wrap items-start justify-center gap-2.5">
                        {activeActionItem.type === 'lesson' && activeActionItem.questId ? (
                          <button
                            type="button"
                            onClick={() => {
                              if (!activeActionItem) return;
                              setActiveQuestId(activeActionItem.questId || null);
                              setActiveChapterId(activeActionItem.chapterId || null);
                              setActiveLessonId(activeActionItem.lessonId || null);
                              setAutoOpenAssign(false);
                              resetActionModal();
                            }}
                            className="group flex w-[48px] cursor-pointer flex-col items-center pt-[1px] text-[#415A88] transition hover:-translate-y-0.5"
                          >
                            <span className="flex h-[40px] w-[40px] items-center justify-center rounded-full border border-[#C9D6F0] bg-white text-[#415A88] shadow-[0_8px_16px_rgba(65,90,136,0.14)] transition-all group-hover:border-[#B8C9EB] group-hover:bg-[#EEF3FF]">
                              <BookOpen className="h-[17px] w-[17px]" />
                            </span>
                          </button>
                        ) : null}
                        {activeActionOptions.map((option) => (
                          <button
                            key={`${activeActionItem?.id || 'item'}-${option.key}`}
                            type="button"
                            onClick={() => {
                              if (!activeActionItem) return;
                              const href = buildActionHref(activeActionItem, option.key);
                              resetActionModal();
                              window.location.href = href;
                            }}
                            className="relative top-[6px] inline-flex h-[31px] min-w-[102px] cursor-pointer items-center justify-center rounded-full border border-[#D8E2F1] bg-[#F7FAFF] px-4.5 text-[12px] font-medium text-[#415A88] transition hover:border-[#C8D6EA] hover:bg-[#EEF4FF]"
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </div>
                {isLoadingActionOptions ? (
                  <div className="mt-4 rounded-[20px] bg-[#F7F8FC] px-4 py-4 text-sm text-[#6B7280]">
                    Loading available modes...
                  </div>
                ) : activeActionError ? (
                  <div className="mt-4 rounded-[20px] bg-[#FEF2F2] px-4 py-4 text-sm text-[#B91C1C]">
                    {activeActionError}
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}

          {canAssignActions ? (
            <div className="mt-5 pb-2 pt-1">
              <div className="flex items-center justify-center">
                <button
                  type="button"
                  onClick={() => {
                    if (!activeActionItem) return;
                    if (!isAuthenticated) {
                      resetActionModal();
                      onAuthRequired?.('assign');
                      return;
                    }

                    if (
                      activeActionItem.type === 'lesson' &&
                      (!activeActionItem.questId || !activeActionItem.chapterId || !activeActionItem.lessonId)
                    ) {
                      toastService.error('This item is missing assignment details. Please try another result.');
                      return;
                    }

                    const target: AssignmentDialogTarget = activeActionItem.type === 'skill'
                      ? {
                          type: 'skill',
                          title: activeActionItem.title,
                          skillId: activeActionItem.id,
                          grade: activeActionItem.defaultGrade,
                          modeOptions: activeAssignModeOptions.filter((mode): mode is AssignableModeOption => mode.key !== 'play'),
                          selectedModes: [],
                        }
                      : {
                          type: 'lesson',
                          title: activeActionItem.title,
                          questId: activeActionItem.questId,
                          chapterId: activeActionItem.chapterId,
                          lessonId: activeActionItem.lessonId,
                          bookName: activeActionItem.bookName || activeActionItem.questTitle,
                          grade: activeActionItem.defaultGrade,
                          modeOptions: activeAssignModeOptions,
                          selectedModes: [],
                        };

                    resetActionModal();
                    void openAssignDialog(target);
                  }}
                  className="inline-flex h-[42px] min-w-[248px] items-center justify-center cursor-pointer rounded-[16px] bg-[#3D3C97] px-6 text-center text-[15px] font-semibold leading-none text-white shadow-[0_12px_24px_rgba(61,60,151,0.24)] transition-transform hover:-translate-y-0.5 hover:bg-[#353485]"
                >
                  Assign to Student
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </PengiModal>

      {canAssignActions && showAssignDialog ? (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/45 px-4" onClick={resetAssignDialog}>
          <div className="w-full max-w-[520px] rounded-[28px] bg-white p-6 shadow-[0_24px_70px_rgba(15,23,42,0.22)]" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-[24px] font-semibold leading-tight text-[#111827]">
                  {assignTarget?.type === 'skill' ? 'Assign Skill' : 'Assign Lesson'}
                </div>
                <div className="mt-2 text-[15px] leading-6 text-[#6B7280]">
                  {stripLessonPrefix(assignTarget?.title) || assignTarget?.title}
                </div>
              </div>
              <button
                type="button"
                onClick={resetAssignDialog}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#F5F7FB] text-[#6B7280] hover:bg-[#EBEEF5]"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="mt-5 rounded-[24px] border border-[#E7EBF3] bg-[#F6F7FB] p-4">
              <div className="mb-3 text-xs font-semibold uppercase tracking-[0.08em] text-[#64748B]">Modes</div>
              <div className="flex flex-wrap gap-2.5">
                {(assignTarget?.modeOptions || []).map((mode) => {
                  const active = Boolean(assignTarget?.selectedModes.includes(mode.key));
                  return (
                    <button
                      key={`search-assign-dialog-${mode.key}`}
                      type="button"
                      onClick={() => {
                        setAssignTarget((prev) => {
                          if (!prev) return prev;
                          const nextModes = prev.selectedModes.includes(mode.key)
                            ? prev.selectedModes.filter((item) => item !== mode.key)
                            : [...prev.selectedModes, mode.key];
                          return {
                            ...prev,
                            selectedModes: nextModes,
                          };
                        });
                      }}
                      className={`rounded-full px-3.5 py-2 text-xs font-semibold transition-colors ${
                        active
                          ? 'bg-[#EAF8E5] text-[#2F7A1E]'
                          : 'bg-white text-[#475569] hover:bg-[#F8FAFC]'
                      }`}
                    >
                      {mode.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-4 rounded-[24px] border border-[#E7EBF3] bg-[#F6F7FB] p-4">
              <div className="mb-3 text-xs font-semibold uppercase tracking-[0.08em] text-[#64748B]">Student</div>
              {isLoadingStudents ? (
                <div className="text-sm text-[#6B7280]">Loading students...</div>
              ) : (
                <select
                  value={selectedChildId}
                  onChange={(event) => setSelectedChildId(event.target.value)}
                  className="w-full rounded-[18px] bg-white px-3 py-3 text-sm text-[#111827] shadow-[0_1px_2px_rgba(15,23,42,0.04)] focus:outline-none"
                >
                  {boundStudents.length === 0 ? (
                    <option value="">No students available</option>
                  ) : (
                    <>
                      {role === 'teacher' && boundStudents.length > 1 && (
                        <option value="__all__">All Students ({boundStudents.length})</option>
                      )}
                      {boundStudents.map((student) => (
                        <option key={student.id} value={student.id}>
                          {student.name}{student.grade ? ` · Grade ${student.grade}` : ''}
                        </option>
                      ))}
                    </>
                  )}
                </select>
              )}
            </div>

            {assignMessage ? (
              <div className="mt-3 rounded-[18px] border border-[#E7EBF3] bg-[#F6F7FB] px-3 py-3 text-sm text-[#475569]">
                {assignMessage}
              </div>
            ) : null}

            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={resetAssignDialog}
                className="rounded-full bg-[#F5F7FB] px-5 py-2.5 text-sm font-semibold text-[#475569] hover:bg-[#EBEEF5]"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!selectedChildId || isLoadingStudents || isAssigning || !(assignTarget?.selectedModes.length)}
                onClick={() => void handleAssignAction()}
                className={`rounded-full px-4 py-2 text-sm font-semibold ${
                  !selectedChildId || isLoadingStudents || isAssigning || !(assignTarget?.selectedModes.length)
                    ? 'cursor-not-allowed bg-[#E5E7EB] text-[#9CA3AF]'
                    : 'cursor-pointer bg-[#5EB53F] px-5 py-2.5 text-white hover:bg-[#4DA033]'
                }`}
              >
                {isAssigning ? 'Assigning...' : 'Assign'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {hasEnoughQuery ? (
        <>
          <div className="rounded-2xl bg-white px-4 pb-3 pt-4 shadow-[0_8px_24px_rgba(34,42,72,0.05)]">
            <div className="flex w-full items-start gap-3">
              <div className="w-full">
                <div
                  className="flex h-[46px] items-center gap-2 rounded-[300px] border bg-white px-4"
                  style={{ borderColor: '#3D3C97' }}
                >
                  <Search className="h-4 w-4 text-[#626992]" />
                  <input
                    value={inputValue}
                    onChange={(event) => setInputValue(event.target.value)}
                    onCompositionStart={handleSearchCompositionStart}
                    onCompositionEnd={handleSearchCompositionEnd}
                    onKeyDown={handleSearchInputKeyDown}
                    placeholder="Search textbook, lesson, skill..."
                    className="w-full bg-transparent text-[15px] text-[#222A48] placeholder:text-[#9AA0C6] focus:outline-none"
                    autoFocus
                  />
                  {inputValue.trim() ? (
                    <button
                      type="button"
                      onClick={() => {
                        setInputValue('');
                        if (query.trim()) {
                          commitSearch('', subjectFilter, gradeFilter);
                        }
                      }}
                      className="inline-flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded-full text-[#7078A8] transition-colors hover:bg-[#F2F4FF]"
                      aria-label="Clear search"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="mt-[12px] flex w-full items-start justify-between gap-3">
              <div className="flex min-w-max items-center gap-4 overflow-x-auto">
                {TAB_ORDER.map((tab) => {
                  const isActive = activeTab === tab;
                  return (
                    <button
                      key={tab}
                      type="button"
                      onClick={() => setActiveTab(tab)}
                      className={`group inline-flex cursor-pointer flex-col items-center px-2.5 py-0 text-[15px] font-medium transition-colors ${
                        isActive
                          ? 'text-[#222A48]'
                          : 'text-[#70779E] hover:text-[#222A48]'
                      }`}
                      style={{ fontFamily: 'Rubik, sans-serif' }}
                    >
                      <span className={isActive ? 'rounded-[999px] bg-[#F1EEFF] px-3 py-0.5' : 'px-1'}>
                        {getTabLabel(tab)}
                      </span>
                      <span
                        className={`mt-0.5 h-[2px] rounded-full transition-all ${
                          isActive ? 'w-[14px] bg-[#5A60E8]' : 'w-0 bg-transparent'
                        }`}
                        style={isActive ? { boxShadow: '0 0 6px rgba(122, 99, 183, 0.35)' } : undefined}
                      />
                    </button>
                  );
                })}
              </div>
              <div className="flex shrink-0 translate-x-[-32px] translate-y-[2px] items-center justify-end gap-2">
                <div onClick={(event) => event.stopPropagation()}>
                  <FilterDropdown
                    label="All subjects"
                    value={subjectFilter}
                    options={SUBJECT_FILTER_OPTIONS}
                    isOpen={openFilterMenu === 'subject'}
                    onToggle={() => setOpenFilterMenu((prev) => (prev === 'subject' ? null : 'subject'))}
                    onSelect={(nextSubject) => {
                      setSubjectFilter(nextSubject);
                      setOpenFilterMenu(null);
                      if (query.trim()) {
                        onSearchStateChange?.({
                          query: query.trim(),
                          subject: nextSubject,
                          grade: gradeFilter,
                          disableCorrection,
                        });
                      }
                    }}
                    widthClass="min-w-[151px]"
                  />
                </div>
                <div onClick={(event) => event.stopPropagation()}>
                  <FilterDropdown
                    label="All grades"
                    value={gradeFilter}
                    options={GRADE_FILTER_OPTIONS}
                    isOpen={openFilterMenu === 'grade'}
                    onToggle={() => setOpenFilterMenu((prev) => (prev === 'grade' ? null : 'grade'))}
                    onSelect={(nextGrade) => {
                      setGradeFilter(nextGrade);
                      setOpenFilterMenu(null);
                      if (query.trim()) {
                        onSearchStateChange?.({
                          query: query.trim(),
                          subject: subjectFilter,
                          grade: nextGrade,
                          disableCorrection,
                        });
                      }
                    }}
                    widthClass="min-w-[141px]"
                  />
                </div>
                {(subjectFilter !== 'all' || gradeFilter !== 'all') ? (
                  <button
                    type="button"
                    onClick={() => {
                      setSubjectFilter('all');
                      setGradeFilter('all');
                      if (query.trim()) {
                        onSearchStateChange?.({
                          query: query.trim(),
                          subject: 'all',
                          grade: 'all',
                          disableCorrection,
                        });
                      }
                    }}
                    className="h-10 cursor-pointer rounded-full bg-[#F5F7FF] px-4 text-sm font-medium text-[#4A507B] shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:bg-[#EBF0FF]"
                  >
                    Clear filters
                  </button>
                ) : null}
              </div>
            </div>

          </div>

          {liveTutorExtensionInstalled === false ? (
            <div className="mt-3 w-full">
              <ExtensionPromoCard compact />
            </div>
          ) : null}
        </>
      ) : null}

      {!hasEnoughQuery && !isLoading && !error ? (
        <div className="bg-white">
          <div className="mx-auto mt-[52px] flex min-h-[540px] w-full max-w-[1100px] flex-col items-center justify-center px-4 text-center [transform:scale(0.9)] [transform-origin:center_top]">
            <div className="text-center">
              <div className="flex items-center justify-center gap-3">
                <div className="relative flex h-10 w-10 translate-x-[3.5px] items-center justify-center">
                  <AppImage
                    src="/assets/landing-logo.png"
                    alt="Pengi"
                    width={36}
                    height={36}
                    className="h-[34px] w-auto object-contain"
                  />
                </div>
                <h1
                  className="text-[34px] font-semibold leading-none tracking-[-0.05em] text-[#222A48] md:text-[46px]"
                  style={{ fontFamily: 'Rubik, sans-serif', letterSpacing: '-0.3px' }}
                >
                  Pengi Search
                </h1>
              </div>
              <p
                className="mx-auto mt-4 w-full max-w-[700px] text-[16px] font-medium italic text-[#747CB0] md:text-[22px]"
                style={{ fontFamily: 'Rubik, sans-serif' }}
              >
                Education-only search for K-12 Students
              </p>
            </div>

            <div className="mt-8 flex w-full max-w-[980px] items-center gap-3">
              <div
                className="flex h-[72px] flex-1 items-center border-[4px] px-7"
                style={{
                  borderColor: '#3D3C97',
                  borderRadius: 170,
                  background: '#FFFFFF',
                  boxShadow: '0 20px 42px rgba(34,42,72,0.08), 0 10px 20px rgba(90,96,232,0.07)',
                }}
              >
                <div className="flex w-full items-center gap-3">
                  <Search className="h-6 w-6 text-[#222A48]" />
                  <input
                    value={inputValue}
                    onChange={(event) => setInputValue(event.target.value)}
                    onCompositionStart={handleSearchCompositionStart}
                    onCompositionEnd={handleSearchCompositionEnd}
                    onKeyDown={handleSearchInputKeyDown}
                    placeholder="Search for lessons, practice problems, simulations..."
                    className="w-full bg-transparent text-[16px] text-[#222A48] placeholder:text-[#969DC7] focus:outline-none md:text-[19px]"
                    style={{ fontFamily: 'Rubik, sans-serif' }}
                    autoFocus
                  />
                  {inputValue.trim() ? (
                    <button
                      type="button"
                      onClick={() => setInputValue('')}
                      className="inline-flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full text-[#7B82AD] transition-colors hover:bg-[#F2F3FF]"
                      aria-label="Clear search"
                    >
                      <X className="h-4.5 w-4.5" />
                    </button>
                  ) : null}
                </div>
              </div>
              <button
                type="button"
                onClick={() => commitSearch(inputValue)}
                className="flex h-[62px] w-[62px] shrink-0 items-center justify-center rounded-full border border-[#222A48] bg-[#222A48] text-white shadow-[0_14px_22px_rgba(34,42,72,0.18)] transition-all hover:translate-y-[-1px] hover:opacity-95"
                aria-label="Search"
              >
                <Search className="h-5.5 w-5.5" strokeWidth={2.8} />
              </button>
            </div>

            <div className="mt-7 w-full max-w-[920px] rounded-[24px] bg-white px-6 py-5 shadow-[0_24px_48px_rgba(34,42,72,0.07)]">
              <p className="mb-3 text-[12px] font-semibold uppercase tracking-[0.12em] text-[#7278A4]">Trending Searches</p>
              <div className="flex flex-wrap items-center justify-center gap-x-2.5 gap-y-2">
                {quickSearches.map((term) => (
                  <button
                    key={term}
                    type="button"
                    onClick={() => handleQuickSearch(term)}
                    className="h-[38px] w-[214px] cursor-pointer rounded-[42px] bg-[#F7F8FF] px-3 text-[14px] font-semibold italic text-[#4A4F7A] shadow-[0_10px_18px_rgba(34,42,72,0.05)] transition-colors hover:bg-[#FBFBFF]"
                    style={{ fontFamily: 'Rubik, sans-serif', fontSize: '14px' }}
                  >
                    {term}
                  </button>
                ))}
              </div>
              <div className="my-5 h-px w-full bg-[#DCE0F7]" />
              <p className="mb-3 text-[12px] font-semibold uppercase tracking-[0.12em] text-[#7278A4]">Browse by Subject</p>
              <div className="flex flex-wrap items-center justify-center gap-2.5">
                {SUBJECT_HERO_BROWSE.map((subject) => {
                  return (
                    <button
                      key={subject.value}
                      type="button"
                      onClick={() => handleSubjectBrowse(subject.value, subject.query)}
                      className={`inline-flex h-[48px] w-[158px] cursor-pointer items-center justify-center rounded-[15px] text-[14px] font-semibold shadow-[0_10px_20px_rgba(22,33,74,0.055)] transition-transform hover:-translate-y-0.5 ${subject.className}`}
                      style={{ fontFamily: 'Rubik, sans-serif' }}
                    >
                      <span>{subject.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            {liveTutorExtensionInstalled === false ? (
              <div className="mt-12 w-full max-w-[900px]">
                <ExtensionPromoCard />
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {shouldShowResultPanel ? (
        <div className="bg-white">
          {!isLoading && !error && correctionBanner ? (
            <div className="mb-4 rounded-2xl border border-[#E8EEF7] bg-[#F8FBFF] px-4 py-3 text-sm text-[#475467]">
              <span>
                Showing results for{' '}
                <button
                  type="button"
                  onClick={() => {
                    setInputValue(correctionBanner.correctedQuery);
                    commitSearch(correctionBanner.correctedQuery);
                  }}
                  className="cursor-pointer font-semibold text-[#1558D6] hover:underline"
                >
                  {correctionBanner.correctedQuery}
                </button>
                .
              </span>
              <span className="ml-2">
                Search instead for{' '}
                <button
                  type="button"
                  onClick={() => {
                    setInputValue(correctionBanner.originalQuery);
                    setDisableCorrection(true);
                    onQueryChange(correctionBanner.originalQuery);
                    onSearchStateChange?.({
                      query: correctionBanner.originalQuery,
                      subject: subjectFilter,
                      grade: gradeFilter,
                      disableCorrection: true,
                    });
                  }}
                  className="cursor-pointer font-semibold text-[#1558D6] hover:underline"
                >
                  {correctionBanner.originalQuery}
                </button>
                .
              </span>
            </div>
          ) : null}
          {isLoading && !hasResults ? (
            <div className="space-y-4">
              <p className="text-sm text-[#6B7280]">Searching...</p>
              <SearchSectionSkeleton type="quest" />
              <SearchSectionSkeleton type="lesson" />
              <SearchSectionSkeleton type="skill" />
              <SearchSectionSkeleton type="web" />
            </div>
          ) : null}
          {isLoading && hasResults ? <p className="text-sm text-[#6B7280]">Updating results...</p> : null}
          {!isLoading && error ? <p className="text-sm text-[#DC2626]">{error}</p> : null}
          {!isLoading && !error && hasEnoughQuery && !hasResults ? (
            <p className="text-sm text-[#6B7280]">No results found.</p>
          ) : null}
          {!isLoading && !error && hasEnoughQuery && hasResults ? (
            <div className="space-y-4">
              {activeTab === 'all' ? (
                <div className="space-y-6 pt-2">
                  {GROUP_ORDER.filter((type) => groupedResults[type].length > 0).map((type) => {
                    const items = groupedResults[type];
                    if (items.length === 0) return null;
                    const previewItems = items.slice(0, type === 'web' ? 3 : type === 'skill' ? 6 : 4);
                    return (
                      <section key={type} className="py-1">
                        <div className="mb-3 flex items-center justify-between gap-3">
                          <span className="inline-flex h-8 items-center rounded-full border border-[#E7EBF5] bg-white px-3.5 text-[15px] font-semibold text-[#2C3553]">
                            {GROUP_META[type].label}
                          </span>
                          {items.length > previewItems.length ? (
                            <SectionLearnMoreButton onClick={() => setActiveTab(type)} />
                          ) : null}
                        </div>
                        {type === 'web' ? (
                          <div className="px-1 py-1">
                            {previewItems.map((item) => (
                              <SearchEngineResultCard
                                key={`${type}-${item.id}`}
                                item={item}
                                onOpenLiveTutorSite={handleOpenLiveTutorSite}
                              />
                            ))}
                          </div>
                        ) : type === 'quest' ? (
                          (() => {
                            const featuredItem =
                              initialPreviewToc && initialPreviewQuest
                                ? previewItems.find((item) => item.id === initialPreviewQuest.id)
                                : null;
                            const restItems = featuredItem
                              ? previewItems.filter((item) => item.id !== featuredItem.id)
                              : previewItems;
                            return (
                              <>
                                {featuredItem && initialPreviewToc ? (
                                  <TextbookFeaturedSection
                                    item={featuredItem}
                                    toc={initialPreviewToc}
                                    onOpen={handleInternalItemClick}
                                  />
                                ) : null}
                                {restItems.length > 0 ? (
                                  <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                                    {restItems.map((item) => (
                                      <TextbookSearchCard
                                        key={`${type}-${item.id}`}
                                        item={item}
                                        onOpen={handleInternalItemClick}
                                      />
                                    ))}
                                  </div>
                                ) : null}
                              </>
                            );
                          })()
                        ) : type === 'skill' ? (
                          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                            {previewItems.map((item) => (
                              <SkillSearchResultCard
                                key={`${type}-${item.id}`}
                                item={item}
                                onOpen={handleInternalItemClick}
                              />
                            ))}
                          </div>
                        ) : type === 'lesson' ? (
                          <div className="grid gap-3 md:grid-cols-2">
                            {previewItems.map((item) => (
                              <LessonSearchResultCard
                                key={`${type}-${item.id}`}
                                item={item}
                                onOpen={handleInternalItemClick}
                              />
                            ))}
                          </div>
                        ) : (
                          <div className="grid gap-3">
                            {previewItems.map((item) => (
                              <InternalSearchResultCard
                                key={`${type}-${item.id}`}
                                item={item}
                                onOpen={handleInternalItemClick}
                              />
                            ))}
                          </div>
                        )}
                      </section>
                    );
                  })}
                </div>
              ) : null}

              {activeTab !== 'all' && !activeTabHasResults ? (
                <p className="text-sm text-[#6B7280]">No {getTabLabel(activeTab).toLowerCase()} results found.</p>
              ) : null}

              {activeTab !== 'all' && activeTabHasResults ? (
                activeTab === 'web' ? (
                  <section className="py-1">
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <span className="text-xs text-[#9CA3AF]">
                        {data?.meta.web?.totalResults ? `About ${data.meta.web.totalResults} results` : ''}
                      </span>
                      {typeof data?.meta.web?.searchTime === 'number' ? (
                        <span className="text-xs text-[#9CA3AF]">{data.meta.web.searchTime}s</span>
                      ) : null}
                    </div>
                    <div className="space-y-2.5">
                      {groupedResults.web.map((item) => (
                        <div
                          key={`web-${item.id}`}
                          className="rounded-xl px-1 py-1 transition-colors hover:bg-[#FAFAFA]"
                        >
                          <SearchEngineResultCard
                            item={item}
                            onOpenLiveTutorSite={handleOpenLiveTutorSite}
                          />
                        </div>
                      ))}
                    </div>
                    <div className="mt-5 flex items-center justify-between">
                      <button
                        type="button"
                        onClick={handleWebPrevPage}
                        disabled={webCurrentPage === 1 || isLoading}
                        className="cursor-pointer rounded-lg border border-[#D1D5DB] px-3 py-1.5 text-xs font-semibold text-[#4B5563] disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        Previous
                      </button>
                      <span className="text-xs text-[#6B7280]">Page {webCurrentPage}</span>
                      <button
                        type="button"
                        onClick={handleWebNextPage}
                        disabled={!data?.meta.web?.nextPageToken || isLoading}
                        className="cursor-pointer rounded-lg bg-[#FA6467] px-3 py-1.5 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        Next
                      </button>
                    </div>
                  </section>
                ) : activeTab === 'quest' ? (
                  <section className="grid gap-4 pt-2 sm:grid-cols-2 xl:grid-cols-4">
                    {groupedResults.quest.map((item) => (
                      <TextbookSearchCard
                        key={`quest-${item.id}`}
                        item={item}
                        onOpen={handleInternalItemClick}
                      />
                    ))}
                  </section>
                ) : activeTab === 'skill' ? (
                    <section className="grid gap-3 pt-2 sm:grid-cols-2 xl:grid-cols-4">
                      {groupedResults.skill.map((item) => (
                        <SkillSearchResultCard
                          key={`skill-${item.id}`}
                        item={item}
                        onOpen={handleInternalItemClick}
                      />
                    ))}
                  </section>
                ) : activeTab === 'lesson' ? (
                  <section className="grid gap-3 pt-2 md:grid-cols-2">
                    {groupedResults.lesson.map((item) => (
                      <LessonSearchResultCard
                        key={`lesson-${item.id}`}
                        item={item}
                        onOpen={handleInternalItemClick}
                      />
                    ))}
                  </section>
                ) : (
                  <section className="grid gap-3 pt-2">
                    {(groupedResults as any)[activeTab].map((item: any) => (
                      <InternalSearchResultCard
                        key={`${activeTab}-${item.id}`}
                        item={item}
                        onOpen={handleInternalItemClick}
                      />
                    ))}
                  </section>
                )
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
